import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * Command-center data layer — CROSS-HUB ROLES ONLY (admin/executive/superadmin).
 *
 * These hooks join client/staff names and read booking_events, which
 * therapist/hub_manager structurally cannot (data-wall.md: no profiles grant,
 * booking_events is admin+ only — 0005/0013). The hub-scoped dashboard keeps
 * using ops-dashboard.hooks.ts, whose selects stay inside the hub_manager
 * column grants. Never point a hub-scoped session at these hooks: the joins
 * would fail RLS, and widening the grants to make them pass would breach the wall.
 */

/** Statuses where the operation is in flight right now. */
export const IN_FLIGHT_STATUSES = [
  "dispatched",
  "en_route_to_client",
  "arrived",
  "in_session",
] as const;

/** Session not started yet — candidate for "overdue" once past scheduled_at. */
export const PRE_SESSION_STATUSES = [
  "pending",
  "awaiting_rider",
  "dispatched",
  "en_route_to_client",
  "arrived",
] as const;

/**
 * Live bookings enriched with the names the ops table shows. profiles has a
 * single FK from bookings (client_id); staff has two (therapist_id/rider_id),
 * so those embeds must name the constraint.
 */
export function useCommandBookings() {
  return useQuery({
    queryKey: ["command-bookings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          `id, ref, status, scheduled_at, created_at, hub_id, payment_method,
           gross_amount, amount_due, booking_group_id,
           client:profiles!bookings_client_id_fkey ( full_name ),
           therapist:staff!bookings_therapist_id_fkey ( full_name ),
           rider:staff!bookings_rider_id_fkey ( full_name ),
           hubs ( name, region ),
           service_variants ( duration_minutes, services ( name ) )`,
        )
        .order("scheduled_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

export type CommandBooking = NonNullable<ReturnType<typeof useCommandBookings>["data"]>[number];

/** Rider FIFO across every hub — available/assigned/offline (0006). */
export function useDispatchQueue() {
  return useQuery({
    queryKey: ["dispatch-queue-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("dispatch_queue")
        .select(
          "rider_id, hub_id, status, booking_id, available_since, staff ( full_name ), hubs ( name )",
        )
        .order("available_since");
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

/**
 * Today's ASAP fairness queue across every hub (migration 0036, Flow 13/14) —
 * the therapist-side counterpart to `useDispatchQueue`'s rider FIFO. A
 * therapist may only ever read their OWN row (`asap_queue_entries_own`,
 * SECURITY §9a — the ranking is built from colleagues' relative earnings);
 * admin/executive/superadmin hold `asap_queue_entries_admin_read` (`using
 * (true)`), so this is exactly the join a hub_manager's own dashboard cannot
 * make cross-hub and stays out of ops-dashboard.hooks.ts for that reason.
 */
export function useAsapQueue() {
  return useQuery({
    queryKey: ["asap-queue-today"],
    queryFn: async () => {
      const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila" }).format(
        new Date(),
      );
      const { data, error } = await supabase
        .from("asap_queue_entries")
        .select("staff_id, hub_id, position, tier, disposition, staff ( full_name ), hubs ( name )")
        .eq("queue_date", today)
        .order("hub_id")
        .order("position");
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

/** Rider trips currently on the road (trips.status = 'started'). */
export function useOngoingTrips() {
  return useQuery({
    queryKey: ["ongoing-trips"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trips")
        .select(
          "id, rider_id, started_at, status, staff ( full_name ), bookings ( ref, hub_id, status )",
        )
        .eq("status", "started")
        .order("started_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

/**
 * Status-transition log for the activity feed. booking_events is written only
 * by Edge Functions (service_role) — it's the factual record of what moved,
 * not a synthetic feed.
 */
export function useBookingEventsFeed() {
  return useQuery({
    queryKey: ["booking-events-feed"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_events")
        .select("id, from_status, to_status, created_at, bookings ( ref, hubs ( name ) )")
        .order("created_at", { ascending: false })
        .limit(30);
      if (error) throw error;
      return data;
    },
    refetchInterval: 15_000,
  });
}

/** Cross-hub remittance rows for the cash summary (crem_admin_read, 0011). */
export function useAllCashRemittances() {
  return useQuery({
    queryKey: ["cash-remittances-all"],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - 7);
      const { data, error } = await supabase
        .from("cash_remittances")
        .select(
          "id, amount, status, created_at, hub_id, hubs ( name ), staff!cash_remittances_therapist_id_fkey ( full_name )",
        )
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    refetchInterval: 30_000,
  });
}

/**
 * Raw 7-day booking window powering the analytics row (bookings-by-hour,
 * busy-hours heatmap, completion rate, top hub/service). One query,
 * aggregated client-side — fine at pilot volume, same tradeoff as
 * useWeeklyBookingStats.
 */
export function useOpsWindow() {
  return useQuery({
    queryKey: ["ops-window-7d"],
    queryFn: async () => {
      const since = new Date();
      since.setDate(since.getDate() - 6);
      since.setHours(0, 0, 0, 0);
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, scheduled_at, status, gross_amount, hub_id, hubs ( name ), service_variants ( services ( name ) )",
        )
        .gte("scheduled_at", since.toISOString())
        .limit(2000);
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export type OpsWindowBooking = NonNullable<ReturnType<typeof useOpsWindow>["data"]>[number];

/** Query keys the realtime channel invalidates — kept next to the hooks they belong to. */
const REALTIME_INVALIDATE_KEYS = [
  ["command-bookings"],
  ["dispatch-queue-all"],
  ["ongoing-trips"],
  ["booking-events-feed"],
  ["cash-remittances-all"],
  ["ops-window-7d"],
  ["attendance"],
  ["weekly-booking-stats"],
  ["ops-bookings"],
] as const;

/**
 * Supabase Realtime → React Query invalidation. Subscribes to postgres_changes
 * on the operational tables and refetches on any event, debounced so a burst
 * of writes causes one refetch. Polling stays on as the fallback: the
 * `supabase_realtime` publication doesn't include these tables yet — enabling
 * it is a migration, and for `bookings` it needs a data-wall review first
 * (postgres_changes delivers whole rows per RLS *policy*; it does not apply
 * hub_manager's column-level grant, so a hub-scoped subscriber would receive
 * walled columns like address_text). That's why this hook must only run for
 * cross-hub roles, which already hold full-row SELECT.
 */
export function useOpsRealtime(enabled: boolean) {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const invalidateAll = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        for (const key of REALTIME_INVALIDATE_KEYS) {
          queryClient.invalidateQueries({ queryKey: [...key] });
        }
      }, 500);
    };

    const tables = ["bookings", "dispatch_queue", "trips", "attendance", "cash_remittances"];
    let channel = supabase.channel("ops-command-center");
    for (const table of tables) {
      channel = channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        invalidateAll,
      );
    }
    channel.subscribe();

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, queryClient]);
}
